import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Shield, X, Loader2, Image as ImageIcon, User as UserIcon,
  Cake, FileText, Globe2, Lock, Users as UsersIcon,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { toast } from 'sonner';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';

const FIELD_ICONS = {
  show_photo: ImageIcon,
  show_name: UserIcon,
  show_age: Cake,
  show_bio: FileText,
  show_country: Globe2,
  show_sex: UsersIcon,
};

function Toggle({ checked, onChange, isDark, testId }) {
  return (
    <button
      type="button"
      onClick={onChange}
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
        checked ? 'bg-emerald-500' : (isDark ? 'bg-white/15' : 'bg-gray-300')
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

/**
 * "Privacy profilo" card + modal.
 * Lets the user toggle which fields (photo/name/age/bio/country+language)
 * are visible on their PUBLIC profile to other users.
 */
export default function PrivacyProfileButton() {
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [settings, setSettings] = useState({
    show_photo: true,
    show_name: true,
    show_age: true,
    show_bio: true,
    show_country: true,
    show_sex: true,
  });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        // Fetch both the per-field toggles and the legacy "private" flag
        // in parallel — they share the same card now.
        const [privacyRes, accountRes] = await Promise.all([
          httpClient.get(`${API_URL}/api/users/me/privacy-settings`),
          httpClient.get(`${API_URL}/api/social/privacy`).catch(() => ({ data: {} })),
        ]);
        if (!cancelled) {
          setSettings((prev) => ({ ...prev, ...privacyRes.data }));
          setIsPrivate(Boolean(accountRes.data?.is_private));
        }
      } catch (_) {
        // Keep defaults on error
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const togglePrivate = async () => {
    const next = !isPrivate;
    setIsPrivate(next); // Optimistic
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/social/privacy`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ is_private: next }),
      });
      if (!res.ok) throw new Error('save failed');
    } catch (_) {
      setIsPrivate(!next);
      toast.error(t('privacy.saveError', 'Errore nel salvataggio'));
    }
  };

  const toggle = async (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next); // Optimistic
    try {
      await httpClient.put(`${API_URL}/api/users/me/privacy-settings`, {
        [key]: next[key],
      });
    } catch (err) {
      // Revert on failure
      setSettings(settings);
      toast.error(err.response?.data?.detail || t('privacy.saveError', 'Errore nel salvataggio'));
    }
  };

  // How many fields are currently hidden → summary in the card subtitle.
  // When the whole account is private, that overrides everything else.
  const hiddenCount = Object.values(settings).filter((v) => v === false).length;
  const subtitle = isPrivate
    ? t('privacy.privateAccount', 'Account privato — solo follower approvati')
    : hiddenCount === 0
      ? t('privacy.allVisible', 'Tutti i campi sono visibili')
      : t('privacy.someHidden', '{{n}} campo/i nascosto/i agli altri utenti', { n: hiddenCount });

  const fieldDefs = [
    { key: 'show_photo',   label: t('privacy.showPhoto', 'Mostra foto profilo') },
    { key: 'show_name',    label: t('privacy.showName', 'Mostra nome') },
    { key: 'show_age',     label: t('privacy.showAge', 'Mostra età') },
    { key: 'show_sex',     label: t('privacy.showSex', 'Mostra genere') },
    { key: 'show_bio',     label: t('privacy.showBio', 'Mostra bio') },
    { key: 'show_country', label: t('privacy.showCountry', 'Mostra paese e lingua') },
  ];

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`w-full glass-card-sm p-4 flex items-center gap-3 transition-colors ${
          isDark ? 'text-white hover:bg-white/5' : 'text-gray-900 hover:bg-gray-50'
        }`}
        data-testid="privacy-profile-btn"
      >
        <Shield className="w-5 h-5 opacity-70" />
        <div className="flex-1 text-left min-w-0">
          <div className="font-medium">{t('privacy.title', 'Privacy profilo')}</div>
          <div className={`text-xs truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {subtitle}
          </div>
        </div>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4"
          onClick={() => setOpen(false)}
          data-testid="privacy-profile-overlay"
        >
          <div
            className={`w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl flex flex-col ${
              isDark ? 'bg-[#1a1a1a] border border-white/10' : 'bg-white border border-gray-200'
            }`}
            style={{ maxHeight: '85vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`px-5 py-4 flex items-center justify-between border-b flex-shrink-0 ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('privacy.title', 'Privacy profilo')}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={isDark ? 'text-white/60 hover:text-white' : 'text-gray-400 hover:text-gray-600'}
                data-testid="privacy-profile-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto">
              <p className={`text-xs mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {t('privacy.description', 'Scegli quali informazioni mostrare sul tuo profilo pubblico. Gli altri utenti vedranno solo i campi abilitati.')}
              </p>

              {loading ? (
                <div className="py-8 flex justify-center">
                  <Loader2 className={`w-5 h-5 animate-spin ${isDark ? 'text-white/60' : 'text-gray-400'}`} />
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Master toggle: private account overrides granular rules */}
                  <div
                    className={`flex items-center gap-3 p-3 rounded-xl border ${
                      isPrivate
                        ? (isDark ? 'bg-[#E8334A]/15 border-[#E8334A]/30' : 'bg-[#E8334A]/10 border-[#E8334A]/30')
                        : (isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200')
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isPrivate ? 'bg-[#E8334A] text-white' : (isDark ? 'bg-white/10 text-white/80' : 'bg-white text-gray-600')
                    }`}>
                      <Lock className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {t('privacy.privateToggle', 'Account privato')}
                      </div>
                      <div className={`text-[11px] ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                        {t('privacy.privateToggleDesc', 'Solo i follower approvati possono vedere i tuoi contenuti')}
                      </div>
                    </div>
                    <Toggle
                      checked={isPrivate}
                      onChange={togglePrivate}
                      isDark={isDark}
                      testId="privacy-toggle-private"
                    />
                  </div>

                  {/* Separator text when private is on — granular toggles become moot */}
                  {isPrivate && (
                    <div className={`text-[11px] text-center py-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      {t('privacy.privateOverride', 'Con l\'account privato queste impostazioni non sono rilevanti')}
                    </div>
                  )}

                  {fieldDefs.map(({ key, label }) => {
                    const Icon = FIELD_ICONS[key];
                    return (
                      <div
                        key={key}
                        className={`flex items-center gap-3 p-3 rounded-xl transition-opacity ${
                          isDark ? 'bg-white/5' : 'bg-gray-50'
                        } ${isPrivate ? 'opacity-50' : ''}`}
                      >
                        {Icon && (
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isDark ? 'bg-white/10 text-white/80' : 'bg-white text-gray-600'
                          }`}>
                            <Icon className="w-4 h-4" />
                          </div>
                        )}
                        <div className={`flex-1 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {label}
                        </div>
                        <Toggle
                          checked={!!settings[key]}
                          onChange={() => toggle(key)}
                          disabled={isPrivate}
                          isDark={isDark}
                          testId={`privacy-toggle-${key}`}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
